import { computed, onMounted, onUnmounted, ref } from 'vue';
import { usePage } from '@inertiajs/vue3';

function makeUrlsAbsolute(css: string, cssUrl: string): string {
    const baseUrl = new URL('.', cssUrl).href;
    return css.replace(/url\(\s*(['"]?)((?!data:|https?:|#)[^)'"]+)\1\s*\)/g, (_, quote, url) => {
        const absoluteUrl = new URL(url.trim(), baseUrl).href;
        return `url(${quote}${absoluteUrl}${quote})`;
    });
}

export function useBuilderTheme() {
    const page = usePage();
    const config = computed(() => page.props.builderTheme ?? { enabled: false });
    const loaded = ref(false);

    const STYLE_ID = 'builder-theme-scoped';
    const EDITOR_STYLE_ID = 'builder-theme-editor';

    const canvasStyle = computed(() => {
        if (!config.value.max_width) return undefined;
        return { '--builder-preview-max-width': config.value.max_width };
    });

    const canvasClass = computed(() => config.value.content_class ?? null);

    async function loadAndScopeCSS() {
        if (!config.value.enabled || !config.value.css_url) return;

        try {
            const response = await fetch(config.value.css_url, {
                headers: { Accept: 'text/css' },
            });
            const css = await response.text();
            const withAbsoluteUrls = makeUrlsAbsolute(css, config.value.css_url);

            // Replace :root with :scope so CSS custom properties apply to the scope root
            const processed = withAbsoluteUrls.replace(/:root/g, ':scope');

            const style = document.createElement('style');
            style.id = STYLE_ID;
            style.textContent = `@scope (#builder-content) {\n${processed}\n}`;
            document.head.appendChild(style);

            loaded.value = true;
        } catch (e) {
            console.warn('Failed to load builder theme CSS:', e);
        }

        if (config.value.editor_css) {
            const editorStyle = document.createElement('style');
            editorStyle.id = EDITOR_STYLE_ID;
            editorStyle.textContent = `#builder-content .ProseMirror { ${config.value.editor_css} }`;
            document.head.appendChild(editorStyle);
        }
    }

    function cleanup() {
        document.getElementById(STYLE_ID)?.remove();
        document.getElementById(EDITOR_STYLE_ID)?.remove();
    }

    onMounted(() => loadAndScopeCSS());
    onUnmounted(() => cleanup());

    return {
        enabled: computed(() => config.value.enabled),
        canvasStyle,
        canvasClass,
        loaded,
    };
}
